import {
  Token,
  createWidget,
  CustomIdentityProvider,
  WidgetInstance,
} from '@livechat/widget-core';
import { store } from '@bonanza-social/toolkit/store';
import { MINUTE_IN_MILLISECONDS } from '@bonanza-social/toolkit/utils';
import { fetchCustomerToken, setVisibility } from './chat.slice';
import { Config } from '@bonanza-social/toolkit/config';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import i18next from '@bonanza-social/i18n';
import { groupIdMapper } from './consts';

const PREFERRED_LANGUAGE_STORAGE_KEY = 'preferredLanguage';

const getCustomIdentityProvider: CustomIdentityProvider = () => {
  let tokenPromise: Promise<Token> | undefined;

  const getToken = async () => {
    if (!tokenPromise) {
      tokenPromise = (async () => {
        const data = await store.dispatch(fetchCustomerToken()).unwrap();

        return data as Token;
      })();
    }

    return tokenPromise;
  };

  return {
    getToken,
    getFreshToken: getToken,
    hasToken: () => Promise.resolve(false),
    invalidate: () => {
      tokenPromise = undefined;

      return Promise.resolve();
    },
  };
};

export const resolveLiveChatGroupId = async (
  groupId?: number,
  language?: string,
): Promise<number> => {
  const preferredLang =
    language ??
    (await AsyncStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY)) ??
    i18next.language;

  const isFrench = preferredLang === 'fr';

  if (isFrench && groupId !== undefined) {
    return groupIdMapper[groupId] ?? groupId;
  }

  if (isFrench) {
    return Number(Config.LIVECHAT_DEFAULT_GROUP_ID_FR);
  }

  return groupId ?? Number(Config.LIVECHAT_DEFAULT_GROUP_ID);
};

export const createChatWidget = async (
  license: string,
  groupId?: number,
  language?: string,
): Promise<WidgetInstance> => {
  const resolvedGroupId = await resolveLiveChatGroupId(groupId, language);

  return new Promise<WidgetInstance>((resolve) => {
    const widget = createWidget({
      license,
      group: `${resolvedGroupId}`,
      customIdentityProvider: getCustomIdentityProvider,
    });

    widget.init();

    widget.updateEventHandlers({
      onVisibilityChanged({ visibility }) {
        store.dispatch(setVisibility(visibility));
      },
      onNewEvent(event) {
        if (event.author.type === 'agent') {
          widget.updateVisibility('maximized');
          window?.navigator?.vibrate?.(200);
        }
      },
      onReady: () => resolve(widget),
    });
  });
};

export const shouldSetSessionVariables = (sessionFieldsTS?: number) =>
  !sessionFieldsTS ||
  Date.now() - sessionFieldsTS >= MINUTE_IN_MILLISECONDS * 2;
